import { Seller, SellerOrder } from '@shopify-multiseller/graphql/partner/types';

/**
 * Has the seller actually been paid for this order?
 *
 * One definition because there were three, and they were the same wrong one:
 * `!!order.payout`, which is true the moment a payout is *assigned*. An order
 * scheduled for payment therefore announced "Payout paid", "You received" and
 * a green "Paid" on the timeline while the seller was still waiting — the
 * screen telling them about money they did not have.
 *
 * There is no status on the payout link in the partner schema (the PayoutStatus
 * enums are declared but nothing carries them), so this reads the date. That is
 * right under either meaning the backend gives it: a date stamped when payment
 * goes out is in the past, and a due date set at scheduling time is in the
 * future. When a status field arrives, this is the one place to change.
 */
export const isPayoutPaid = (order: Pick<SellerOrder, 'payout'>): boolean => {
  const paidOn = order.payout?.payoutDate ? new Date(order.payout.payoutDate) : undefined;
  return !!paidOn && paidOn.getTime() <= Date.now();
};

/** Why a payout cannot leave yet, when the reason is the seller's own account. */
export type PayoutMethodState = 'ready' | 'missing' | 'incomplete';

/**
 * Can money actually reach this seller?
 *
 * Fulfilling an order pays nobody if there is nowhere to send the money, and
 * nothing on the order said so — the timeline promised a payout was "Pending"
 * whether the seller had a bank account attached or had never opened the payout
 * settings. A Stripe account that exists but still has requirements outstanding
 * is the same problem in different clothes: Stripe holds the money until they
 * are cleared.
 */
export const payoutMethodState = (
  seller?: Pick<Seller, 'payoutAccount'> | null,
): PayoutMethodState => {
  const account = seller?.payoutAccount;
  if (!account) return 'missing';
  if (account.__typename === 'StripePayoutAccount' && (account.requirementsDue || !account.active)) {
    return 'incomplete';
  }
  return 'ready';
};
